import { useState, useEffect } from 'react';
import { detectGPUCapability, type GPUCapability } from '../../lib/webgpu-detect';
import type { DemoType } from '@shared/types/product.types';

interface DemoEmbedProps {
  demoType: DemoType;
  demoUrl: string | null;
}

export function DemoEmbed({ demoType, demoUrl }: DemoEmbedProps) {
  const [capability, setCapability] = useState<GPUCapability | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (demoType === 'WEBGPU') {
      detectGPUCapability().then((cap) => {
        setCapability(cap);
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [demoType]);

  if (demoType === 'NONE' || !demoUrl) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="aspect-video animate-pulse rounded-xl bg-navy-800" data-testid="demo-embed" data-demo-state="loading" />
    );
  }

  if (demoType === 'WEBGPU' && capability && !capability.webgpu) {
    return (
      <div className="rounded-xl border border-yellow-600/30 bg-yellow-600/10 p-6 text-center" data-testid="demo-embed" data-demo-state="unsupported">
        <p className="text-yellow-400">WebGPU is not supported in your browser.</p>
        <p className="mt-2 text-sm text-gray-400">
          Try Chrome 113+ or Edge 113+ for the best experience.
        </p>
      </div>
    );
  }

  if (demoType === 'IFRAME' || demoType === 'WEBGPU') {
    return (
      <div className="overflow-hidden rounded-xl border border-white/10" data-testid="demo-embed" data-demo-state="loaded">
        <iframe
          src={demoUrl}
          className="aspect-video w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
          sandbox="allow-scripts allow-same-origin"
          title="Product Demo"
        />
      </div>
    );
  }

  if (demoType === 'CANVAS') {
    return (
      <div className="overflow-hidden rounded-xl border border-white/10" data-testid="demo-embed" data-demo-state="loaded">
        <iframe
          src={demoUrl}
          className="aspect-video w-full"
          allow="accelerometer; autoplay"
          sandbox="allow-scripts allow-same-origin"
          title="Canvas Demo"
        />
      </div>
    );
  }

  return null;
}
